import { useReactFlow } from "@xyflow/react";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";

import { withConfigurationIdToNameRemapping } from "../../utils";
import useAllSources from "../hooks/useAllSources";
import useSources from "../hooks/useSources";
import { useWorkflow } from "../workflow-context";

import { displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";
import SidebarWithBlur from "@/components/ui/Sidebar/SidebarWithBlur";
import { BlockType, blockTypeToRawBlockType } from "@/components/PowerBlock/Blocks";
import { BlockEnv } from "@/components/PowerBlock/Block/env-context";
import PowerBlock from "@/components/PowerBlock";

interface WorkflowSidebarProps {
  readonly?: boolean;
}

const MIN_SIDEBAR_WIDTH = 500;
const MAX_SIDEBAR_WIDTH = 800;

const WorkflowSidebar = observer((props: WorkflowSidebarProps) => {
  const { readonly } = props;
  const workflow = useWorkflow();
  const { isSidebarOpen, activeNode } = workflow;
  const { updateNode } = useReactFlow();

  const handleCloseSidebar = () => {
    runInAction(() => {
      workflow.setActiveNode(undefined);
      workflow.closeSidebar();
    });
  };

  const sources = useSources();
  const allSources = useAllSources();

  const saveToSession = async (automatic = false) => {
    const newReference = workflow.getNodeById(activeNode?.id ?? "");
    try {
      if (activeNode) {
        const success = await workflow.saveNode(newReference!);
        if (success) {
          displayToast(
            automatic ? "Changes saved automatically" : "Node saved successfully",
            ToastType.success,
          );
          if (!automatic) {
            handleCloseSidebar();
          }
        }
      }
    } catch {
      displayToast("Failed to save node", ToastType.error);
    }
  };

  return (
    <SidebarWithBlur
      isOpen={isSidebarOpen}
      onClose={handleCloseSidebar}
      minWidth={MIN_SIDEBAR_WIDTH}
      maxWidth={MAX_SIDEBAR_WIDTH}
      defaultWidth={MIN_SIDEBAR_WIDTH}
      position="absolute"
      resizable
      contentClassName="nokey"
      enableEscapeKey
      enableClickOutside={false}
      hasFooter={!readonly}
    >
      {activeNode && (
        <PowerBlock
          configuration={(() => {
            const { position, ...configWithoutPosition } = activeNode.configuration || {};
            return withConfigurationIdToNameRemapping(configWithoutPosition, workflow.nodes);
          })()}
          isDraft={!activeNode.saved}
          env={BlockEnv.AGENT}
          key={`${activeNode?.id}_${activeNode?.node_type}`}
          // onAutoSave={({ name, configuration }) => {
          //   workflow.updateNode(activeNode.name, (node) => ({
          //     ...node,
          //     name,
          //     configuration,
          //   }));
          //   saveToSession(true);
          // }}
          onSave={({ name, configuration }) => {
            workflow.updateNode(activeNode.name, node => ({
              ...node,
              name,
              configuration: {
                ...configuration,
                position: activeNode?.configuration.position,
              },
            }));
            saveToSession();
          }}
          onClose={handleCloseSidebar}
          readonly={readonly}
          name={activeNode?.name}
          type={BlockType[activeNode?.node_type as keyof typeof BlockType]}
          sources={sources}
          allSources={allSources}
          onTypeChange={type => {
            const rawBlockType = blockTypeToRawBlockType(type);
            const currentPosition = activeNode?.configuration?.position;

            workflow.updateNode(activeNode?.name, node => ({
              ...node,
              configuration: {
                position: node.configuration?.position || currentPosition,
              },
              node_type: rawBlockType,
              saved: false,
            }));

            updateNode(activeNode?.id as string, {
              type: rawBlockType,
              data: {
                type: rawBlockType,
                position: currentPosition,
              },
            });
          }}
        />
      )}
    </SidebarWithBlur>
  );
});

export default WorkflowSidebar;
